import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { Check, Star } from 'lucide-react';
const benefits = ['Tight areas soften instead of bracing', 'Muscles relax instead of guarding', 'Joints feel less compressed', 'Comfort builds instead of fading', 'Skin feels clean and normal'];
const testimonials = [{
  text: "Smells great and absorbs quickly. I've been using it daily and really like how my joints feel afterward.",
  author: 'Kimberly C.',
  rating: 5
}, {
  text: "Arrived very fast. I've been applying it once a day and can already feel less stiffness. Looking forward to continued use.",
  author: 'Janette C.',
  rating: 5
}, {
  text: 'Feels soothing and comforting. Great quality for the price.',
  author: 'Sylvia N.',
  rating: 5
}, {
  text: "Excellent product. Works well and doesn't feel greasy.",
  author: 'Felix R.',
  rating: 5
}, {
  text: 'We love this oil. The warming sensation is gentle and very relaxing.',
  author: 'Olivia C.',
  rating: 5
}, {
  text: 'Perfect timing. This has become part of my nightly routine.',
  author: 'Lorna D.',
  rating: 5
}, {
  text: 'I love it. Helps me feel more comfortable after long days.',
  author: 'William M.',
  rating: 5
}, {
  text: 'Very pleased with this product. Packaged well and works as described.',
  author: 'Pamela M.',
  rating: 5
}, {
  text: 'Great consistency and scent. I noticed relief with regular use.',
  author: 'Cynthia W.',
  rating: 5
}, {
  text: 'Love it. Gentle but effective.',
  author: 'Sonya A.',
  rating: 5
}, {
  text: "I've been a customer for a while and Ginext never disappoints. Consistent quality and reliable delivery.",
  author: 'Gerard J.',
  rating: 5
}, {
  text: 'Makes my joints feel looser and more relaxed, especially after movement.',
  author: 'David R.',
  rating: 5
}];
interface BodyReliefBenefitsSectionProps {
  beforeImage?: string;
  afterImage?: string;
}
export function BodyReliefBenefitsSection({
  beforeImage,
  afterImage
}: BodyReliefBenefitsSectionProps = {}) {
  const defaultBeforeImage = 'https://cdn.magicpatterns.com/uploads/oEdYBNW3tghKx5wYvU5m28/u8896349413_Photorealistic_close-up_image_of_a_55-year-old_wo_0f2373d2-1a79-4c27-93c1-014cf3bebbf7_3.png';
  const defaultAfterImage = 'https://cdn.magicpatterns.com/uploads/vfjPdru16qJevbYFbuPQKh/u8896349413_Photorealistic_close-up_image_of_the_same_55-year_3b1f0418-41bb-493a-9df6-0b0ae4dc5e3c_3.png';
  return <>
      <section className="py-8 sm:py-12 bg-charcoal text-bone" data-id="element-122">
        <div className="max-w-6xl mx-auto px-4" data-id="element-123">
          {/* Header Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 items-center mb-8 sm:mb-12" data-id="element-124">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.6
          }} data-id="element-125">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-light mb-4 sm:mb-6 tracking-tight" data-id="element-126">
                WHAT YOU'LL FEEL (AND WHAT YOU WON'T)
              </h2>
              <div className="w-12 sm:w-16 h-px bg-herbal/30 mb-4 sm:mb-6" data-id="element-127" />
              <p className="text-sm sm:text-base text-gray-400 leading-relaxed font-light" data-id="element-128">
                You won't feel freezing. You won't feel aggressive tingling.
                <br data-id="element-129" />
                <br data-id="element-130" />
                You'll feel warmth arrive gradually — then deepen.
              </p>
            </motion.div>

            <div className="space-y-3 sm:space-y-4" data-id="element-131">
              {benefits.map((benefit, index) => <motion.div key={index} initial={{
              opacity: 0,
              x: 30
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true,
              margin: '-50px'
            }} transition={{
              duration: 0.4,
              delay: index * 0.05
            }} className="flex items-start gap-3" data-id="element-132">
                  <div className="mt-1 w-5 h-5 rounded-full bg-herbal/20 flex items-center justify-center flex-shrink-0" data-id="element-133">
                    <Check className="w-3 h-3 text-herbal" data-id="element-134" />
                  </div>
                  <p className="text-sm sm:text-base font-light" data-id="element-135">{benefit}</p>
                </motion.div>)}
            </div>
          </div>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} className="text-center text-sm sm:text-base text-gray-400 font-light italic" data-id="element-136">
            This is relief that fits daily life, not emergency moments only.
          </motion.p>

          {/* Before/After Images */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }} className="max-w-4xl mx-auto mt-8 sm:mt-12" data-id="element-137">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8" data-id="element-138">
              {/* Before Image */}
              <div className="relative" data-id="element-139">
                <p className="text-gray-400 text-sm sm:text-base font-light mb-3 text-center" data-id="element-140">
                  Before GINEXT
                </p>
                <div className="relative rounded-2xl overflow-hidden shadow-2xl" data-id="element-141">
                  <img src={beforeImage || defaultBeforeImage} alt="Before GINEXT use" className="w-full h-auto" loading="lazy" data-id="element-142" />
                  <div className="absolute inset-0 bg-gradient-to-tr from-ginger/20 via-transparent to-transparent mix-blend-overlay pointer-events-none" data-id="element-143" />
                </div>
              </div>

              {/* After Image */}
              <div className="relative" data-id="element-144">
                <p className="text-herbal text-sm sm:text-base font-normal mb-3 text-center" data-id="element-145">
                  After GINEXT use
                </p>
                <div className="relative rounded-2xl overflow-hidden shadow-2xl" data-id="element-146">
                  <img src={afterImage || defaultAfterImage} alt="Body relaxed and comfortable after GINEXT use" className="w-full h-auto" loading="lazy" data-id="element-147" />
                </div>

                {/* Floating badge */}
                <div className="absolute bottom-4 left-4 bg-white/50 backdrop-blur-md p-2 sm:p-2.5 rounded-lg shadow-md border border-bone-dark/40 max-w-[100px] sm:max-w-[120px]" data-id="element-148">
                  <p className="text-ginger font-normal text-xs sm:text-sm leading-tight" data-id="element-149">
                    100% Natural
                  </p>
                  <p className="text-charcoal text-[10px] sm:text-xs font-light" data-id="element-150">
                    Zero synthetics
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-6 sm:py-8 bg-bone" data-id="element-151">
        <div className="max-w-6xl mx-auto px-4" data-id="element-152">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.6
        }} className="text-center mb-6 sm:mb-8" data-id="element-153">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-light text-charcoal mb-2 tracking-tight" data-id="element-154">
              VERIFIED CUSTOMER TESTIMONIALS
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4" data-id="element-155">
            {testimonials.map((testimonial, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} className="bg-white rounded-lg p-3 sm:p-4 shadow-md border border-bone-dark" data-id="element-156">
                {/* Star Rating */}
                <div className="flex gap-0.5 mb-2" data-id="element-157">
                  {[...Array(testimonial.rating)].map((_, i) => <Star key={i} className="w-3 h-3 fill-ginger text-ginger" data-id="element-158" />)}
                </div>

                {/* Quote */}
                <p className="text-sm sm:text-base font-normal text-charcoal mb-2" data-id="element-159">
                  "{testimonial.text}"
                </p>

                {/* Detail */}
                <p className="text-xs text-charcoal-light leading-relaxed mb-2 font-light" data-id="element-160">
                  {testimonial.detail}
                </p>

                {/* Author */}
                <p className="text-xs font-normal text-ginger" data-id="element-161">
                  {testimonial.author}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>
    </>;
}